// ---------------------------------------------------------------------------
// RENDERING A DATE AND RENDERING MONEY — in one place, on purpose.
//
// Both of these lived inside screens/Events.tsx. V11 needs the same two
// functions, and the alternative to lifting them out was a second copy — which
// for `money` in particular is not a tidiness question but a correctness one.
//
// THE DIVISION IS THE PART WORTH WATCHING. Currencies with other than two minor
// digits exist (JPY has none, KWD has three), and a hard-coded `/ 100` silently
// misprices them by a factor of a hundred or a thousand. Two copies of that
// function are two chances to write the constant, and V32 invoices from these
// numbers.
//
// NOTHING HERE IS A PRODUCT RULE. These are presentation helpers with no
// vocabulary in them: no state name, no role, no currency list.
// ---------------------------------------------------------------------------

import { intlTag, translate, type Locale } from "../i18n/translate";

// SWEEP §4. THE READER'S LOCALE, SET BY THE SHELL WITH `<html lang>` and, like
// the browser default it stands in for, a property of the page rather than of
// a call. English renders through `undefined`, the reader's own browser locale,
// exactly as before.
let reader: Locale = "en";
export function setReaderLocale(locale: Locale): void {
  reader = locale;
}

/** The Intl tag for the reader's locale, for a formatter that lives outside this file. */
export function readerIntlTag(): string | undefined {
  return intlTag(reader);
}

/** A month the database calls by its first instant (2026-10-01T00:00:00.000Z), as the reader writes it. UTC, because the instant is a label for the month and not a moment in anybody's day. */
export function monthLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(readerIntlTag(), { month: "long", year: "numeric", timeZone: "UTC" });
}

// ONE OPTIONS OBJECT FOR BOTH RENDERINGS. `whenIn` decides whether the two
// zones agree by comparing what they RENDER, so the two calls must differ in
// nothing but the zone — two copies of this literal is how they come to differ
// in a `year` nobody noticed, and the answer would be "they disagree" forever.
const STAMP: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

/** A timestamp, in the reader's own locale and timezone. */
export function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(intlTag(reader), STAMP);
}

/**
 * A short stamp for a list row, in the organisation's clock and with no zone name: "24 Oct, 15:00".
 * Every row of a list is in the same zone, so the abbreviation only crowds the column; the
 * event's own page keeps the full stamp (whenIn). Tour T-37.
 */
export function whenCompact(iso: string, zone: string | null | undefined): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleString(intlTag(reader), {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", ...(zone ? { timeZone: zone } : {}),
    });
  } catch {
    return when(iso);
  }
}

/**
 * A timestamp in the ORGANISATION'S clock, with the reader's own in brackets.
 *
 * OWNER RULING, 15 August, recorded in `HANDOFF.md` §6. **A shift happens where
 * the venue is.** A scheduler in Budapest and a steward in London discussing the
 * same start have to see the same number, and until V27a they did not: every
 * console screen rendered through `when()`, which formats in the READER'S
 * browser, while every sentence the database computes about an hour of the day
 * is computed in the organisation's zone.
 *
 * V29's Risk Radar is where that became visible AND WRONG. On one row the `WHEN`
 * column read *06:00* and the working underneath it read *this shift starts at
 * 05:00 local time* — both correct, on a screen whose entire subject is early
 * starts. `0084` resolves `app.organisation.timezone` for exactly this reason,
 * and `0062`'s `proof_ts` ruling is the same rule one slice earlier.
 *
 * THE BRACKET IS DROPPED WHERE THE TWO ZONES AGREE, which is the ordinary case
 * for a single-country tenant. That is not tidiness: the bracketed half roughly
 * doubles the width of a time cell, and this is read on a forty-row table and on
 * an ops-room wall where `11-…` asks for large type. A second clock that always
 * says the same thing as the first is a column nobody reads.
 *
 * THE ZONE NAME IS ALWAYS SHOWN, even when they agree. A time with no zone on it
 * is the state this ruling exists to leave behind.
 *
 * TWO FALLBACKS, BOTH TO THE READER'S OWN CLOCK. A payload that carries no zone,
 * and a zone string Intl refuses, both render exactly as `when()` did before —
 * because a screen that throws is worse than a screen that is merely as good as
 * yesterday's, and `timezone` is optional on the client's `Me` type by design.
 */
export function whenIn(iso: string, zone: string | null | undefined): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  if (!zone) return when(iso);

  let there: string;
  let thereBare: string;
  try {
    there = d.toLocaleString(intlTag(reader), { ...STAMP, timeZone: zone, timeZoneName: "short" });
    thereBare = d.toLocaleString(intlTag(reader), { ...STAMP, timeZone: zone });
  } catch {
    // RangeError — an invalid IANA name. `app.is_iana_time_zone` guards what
    // goes into the column, so this is a malformed payload rather than an
    // ordinary state, and it must not blank a scheduler's screen.
    return when(iso);
  }

  // THE COMPARISON IS OVER THE RENDERED WALL CLOCK, not over offsets. Two zones
  // that agree today can disagree in three weeks — Europe/London and
  // Europe/Dublin never, Europe/London and Atlantic/Reykjavik in summer only —
  // so the question is asked about THIS INSTANT and answered for this row alone.
  if (thereBare === when(iso)) return there;
  return `${there} (${translate(reader, "{time} your time", { time: clock(iso) })})`;
}

/** Just the clock part, for a row that already says which day it is. */
/**
 * A shift or event window for a list: "Sep 21, 10:00–19:00" in the organisation's clock, and, only
 * when the reader's own clock shows a different time, the start in theirs ("11:00 your time").
 * Tour T-50: the full stamp with a zone name wrapped onto five lines in a portal table.
 */
export function whenRange(startIso: string, endIso: string, zone: string | null | undefined): { main: string; yours: string | null } {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return { main: "—", yours: null };
  const wall = (d: Date, z: string | undefined) =>
    d.toLocaleTimeString(intlTag(reader), { hour: "2-digit", minute: "2-digit", ...(z ? { timeZone: z } : {}) });
  const z = zone ?? undefined;
  const day = start.toLocaleDateString(intlTag(reader), { month: "short", day: "numeric", ...(z ? { timeZone: z } : {}) });
  const main = `${day}, ${wall(start, z)}–${wall(end, z)}`;
  const differs = z !== undefined && wall(start, z) !== wall(start, undefined);
  return { main, yours: differs ? translate(reader, "{time} your time", { time: wall(start, undefined) }) : null };
}

export function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString(intlTag(reader), { hour: "2-digit", minute: "2-digit" });
}

/**
 * Minor units, rendered.
 *
 * Intl does the grouping and the symbol; the exponent comes from ISO_4217_EXPONENT
 * below rather than an assumed 100, which is what makes this correct for a currency
 * with no minor unit (JPY, 0) and for one with three (KWD).
 *
 * WHAT THE `catch` IS ACTUALLY FOR — measured at V11, and it is not what this
 * comment said when it was written at V10.
 *
 * The old wording claimed the fallback handled "an unknown currency code". It
 * does not, because Intl does not throw on one: `currency: "ZZZ"` resolves
 * happily, uses two fraction digits and prints the code where a symbol would
 * go. So that branch was unreachable for the case it named, and the case that
 * DOES reach it had never been considered — a MALFORMED code. `"Z"`, `"zzzz"`
 * or an empty string raise RangeError, and those are what a truncated column or
 * a mis-mapped field produces. Showing the raw minor units with the string
 * beside them is honest: it is visibly not money, which is the correct
 * impression when the currency is not a currency.
 *
 * Found by writing the test, not by reading the code. The function's behaviour
 * never changed; only the claim about it did.
 *
 * S38: THE ONLY `money`. `system/pay.ts` had a second, exact on a string but
 * blind to the exponent; this one knew the exponent but divided a float. Now the
 * minor units (a `bigint` arrives as a string, and stays one) become a decimal
 * STRING by moving the point, and Intl formats that string exactly — so neither
 * a large amount nor a JPY one is mispriced. Anything that is not an integer
 * takes the fallback, for the same reason a malformed code does.
 */
/**
 * THE EXPONENT IS THE DATABASE'S, WRITTEN DOWN, NOT ASKED OF THE BROWSER (21 October 2026).
 *
 * Both functions below used to ask Intl how many digits a currency has. Intl's answer for the forint changed
 * under us: Chrome 152 and Node 26 report HUF's cash digits, 0, in every locale, while the database stores the
 * forint in fillér (ISO 4217's two digits; migration 0265 multiplies the decree's forints by 100, and the seed's
 * hourly rate is 280000). So a 2 500 Ft parking ticket displayed as "250 000 Ft", and a typed "2500" forint rate
 * was stored as 25 Ft. The minor unit is part of the data contract, so it is a table here: ISO 4217's list of
 * currencies whose minor unit is not two digits. Everything else has two. Measured in
 * test/money-forint.test.ts.
 */
const ISO_4217_EXPONENT: Readonly<Record<string, number>> = {
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0, PYG: 0, RWF: 0, UGX: 0, UYI: 0, VND: 0, VUV: 0,
  XAF: 0, XOF: 0, XPF: 0,
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
  CLF: 4, UYW: 4,
};

function exponentOf(currency: string): number {
  return ISO_4217_EXPONENT[currency] ?? 2;
}

// The forint is written without decimals (event.clinic's tour, entries 9, 10 and 56: "350 000 Ft", "HUF 350,000"),
// but only when there are no fillér to show: a real fillér amount keeps them rather than being rounded away.
const WHOLE_WHEN_ROUND = new Set(["HUF"]);

export function money(minor: number | string | null, currency: string | null): string {
  if (minor === null || currency === null) return "—";
  const raw = String(minor);
  try {
    if (!/^-?\d+$/.test(raw)) throw new RangeError("not an integer number of minor units");
    const digits = exponentOf(currency);
    const sign = raw.startsWith("-") ? "-" : "";
    const units = raw.replace("-", "").padStart(digits + 1, "0");
    const point = units.length - digits;
    const fraction = units.slice(point);
    const shown = WHOLE_WHEN_ROUND.has(currency) && /^0*$/.test(fraction) ? 0 : digits;
    const format = new Intl.NumberFormat(intlTag(reader), {
      style: "currency",
      currency,
      minimumFractionDigits: shown,
      maximumFractionDigits: shown,
    });
    const decimal = shown === 0 ? units.slice(0, point) : `${units.slice(0, point)}.${fraction}`;
    // Intl formats a decimal string without passing it through a float; the
    // ES2022 lib types only know the number overload.
    return format.format(`${sign}${decimal}` as unknown as number);
  } catch {
    return `${raw} ${currency}`;
  }
}

/**
 * Basis points as a percentage, to two places.
 *
 * ★ THE DIVISION LIVES HERE FOR `money`'s REASON, and it is the only division
 * V32 puts in the browser. `routes/invoicing.ts` and `api/invoicing.ts` are
 * grepped for arithmetic operators after comment-stripping, because a margin
 * computed in TypeScript from a rate and some hours would be a second engine.
 * Turning 4928 into "49.28%" is not computing a margin — it is the same
 * presentation step `money` performs on minor units — and putting it in the one
 * shared formatting module is what keeps that distinction visible rather than
 * making it an exception somebody argues for later.
 *
 * THE SERVER STILL DECIDES THE FIGURE. A screen that divided a margin by a
 * charge would be the thing this file exists to prevent.
 */
export function percent(basisPoints: number | null): string {
  if (basisPoints === null || !Number.isFinite(basisPoints)) return "—";
  // "27%" or "12.5%" ("12,5%" in Hungarian), never "27.00%" (event.clinic's tour, entry 74).
  return `${(basisPoints / 100).toLocaleString(intlTag(reader), { maximumFractionDigits: 2 })}%`;
}

/**
 * A span between two instants, in words.
 *
 * Rounded to the nearest minute and stated in hours and minutes, because a
 * shift length is read at a glance and "8h 30m" is read faster than "510".
 */
export function span(startIso: string, endIso: string): string {
  const a = Date.parse(startIso);
  const b = Date.parse(endIso);
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return "—";
  const minutes = Math.round((b - a) / 60000);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return translate(reader, "{m}m", { m: rest });
  return rest === 0 ? translate(reader, "{h}h", { h: hours }) : translate(reader, "{h}h {m}m", { h: hours, m: rest });
}

/**
 * A count of minutes as hours and minutes, with the plain minute total in brackets once it
 * is an hour or more: "1h 30m (90 min)". Owner, 22 October 2026 (T-66): the long form is read
 * at a glance, the bracket is what a rate or a payroll sheet is checked against.
 */
export function minutesLong(minutes: string | number | null): string {
  if (minutes === null) return "—";
  const total = Math.trunc(Number(minutes));
  if (!Number.isFinite(total)) return "—";
  const hours = Math.trunc(total / 60);
  const rest = total - hours * 60;
  if (hours === 0) return translate(reader, "{m}m", { m: rest });
  const long = rest === 0 ? translate(reader, "{h}h", { h: hours }) : translate(reader, "{h}h {m}m", { h: hours, m: rest });
  return `${long} (${translate(reader, "{n} min", { n: total })})`;
}

/**
 * NOW, PLUS AN ELAPSED OFFSET, AS A VALUE A `datetime-local` INPUT ACCEPTS.
 *
 * THE STANDARD IDIOM FOR THIS IS WRONG AND IT WAS IN THREE SCREENS.
 *
 *     new Date(t.getTime() - t.getTimezoneOffset() * 60_000).toISOString()
 *
 * shifts an instant by the offset that applies AT THAT INSTANT and then reads
 * it back as if it were UTC. It is right today and wrong on the two mornings a
 * year when the offset the browser reports and the offset that applied at the
 * target instant are not the same number — and "wrong by an hour, twice a year,
 * silently" is the entire defect class V12 exists to prevent. Three copies of
 * it were in the tree, which is what made it worth one shared function.
 *
 * `toLocaleString("sv-SE")` asks Intl to render the instant in the viewer's own
 * zone and returns `YYYY-MM-DD HH:MM:SS` — the one locale whose date format is
 * already ISO 8601. No arithmetic on a wall-clock value at all: the conversion
 * is done by the zone's real rules, which is the same rule the database follows.
 *
 * `offsetHours` is added to an INSTANT, which is legitimate — an hour is an
 * hour, and this is "two days from now", not "the same clock time on Sunday".
 */
export function localDateTimeInput(offsetHours = 0): string {
  return new Date(Date.now() + offsetHours * 3_600_000)
    .toLocaleString("sv-SE")
    .replace(" ", "T")
    .slice(0, 16);
}

/** Today in the viewer's own zone, as a value a `date` input accepts. */
export function localDateInput(): string {
  return new Date().toLocaleDateString("sv-SE");
}

/**
 * How many minor units are in one major unit of this currency.
 *
 * FROM THE SAME TABLE AS `money`, and for the same reason. A hard-coded 100
 * misprices JPY by a hundred and KWD by ten, and asking Intl misprices the forint
 * by a hundred in today's browsers; this is the function that turns what somebody
 * typed into what gets invoiced.
 */
export function minorPerMajor(currency: string): number {
  return 10 ** exponentOf(currency);
}

// A cell a spreadsheet would run as a formula (a client named "=HYPERLINK(...)")
// is defused with a leading apostrophe. A negative number such as a credit
// line's "-1200" is left alone.
export function csvCell(raw: string): string {
  const v = /^[=+@\t\r]|^-(?![\d.])/.test(raw) ? `'${raw}` : raw;
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Rows of cells as CSV text. */
export function csv(rows: string[][]): string {
  return rows.map((r) => r.map(csvCell).join(",")).join("\n");
}
